import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import Placeholder from "@tiptap/extension-placeholder"
import ButtonNode from "../extensions/button_node"

export default class extends Controller {
  static targets = ["editor", "input", "source", "toolbar"]

  declare editorTarget: HTMLElement
  declare inputTarget: HTMLInputElement
  declare sourceTarget: HTMLTextAreaElement
  declare toolbarTarget: HTMLElement
  declare hasSourceTarget: boolean

  private editor: Editor | null = null
  private sourceMode = false

  connect() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3, 4] },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { class: "text-blue-600 underline" },
        }),
        Image.configure({
          inline: false,
          allowBase64: false,
        }),
        ButtonNode,
        Placeholder.configure({
          placeholder: "Start writing...",
        }),
      ],
      content: this.inputTarget.value || "",
      editorProps: {
        attributes: {
          class: "prose max-w-none focus:outline-none min-h-[200px] p-4",
        },
      },
      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.getHTML()
      },
    })
  }

  disconnect() {
    this.editor?.destroy()
  }

  // Toolbar actions
  bold() { this.editor?.chain().focus().toggleBold().run() }
  italic() { this.editor?.chain().focus().toggleItalic().run() }
  strike() { this.editor?.chain().focus().toggleStrike().run() }

  heading(event: Event) {
    const level = parseInt((event.currentTarget as HTMLElement).dataset.level || "2") as 2 | 3 | 4
    this.editor?.chain().focus().toggleHeading({ level }).run()
  }

  bulletList() { this.editor?.chain().focus().toggleBulletList().run() }
  orderedList() { this.editor?.chain().focus().toggleOrderedList().run() }
  blockquote() { this.editor?.chain().focus().toggleBlockquote().run() }
  codeBlock() { this.editor?.chain().focus().toggleCodeBlock().run() }
  horizontalRule() { this.editor?.chain().focus().setHorizontalRule().run() }

  link() {
    const previousUrl = this.editor?.getAttributes("link").href
    const url = window.prompt("Enter URL:", previousUrl || "https://")
    if (url === null) return
    if (url === "") {
      this.editor?.chain().focus().extendMarkRange("link").unsetLink().run()
    } else {
      this.editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
    }
  }

  image() {
    const url = window.prompt("Enter image URL:")
    if (url) {
      this.editor?.chain().focus().setImage({ src: url }).run()
    }
  }

  insertButton() {
    const label = window.prompt("Button text:", "Click here")
    if (label === null) return
    const href = window.prompt("Button URL:", "https://")
    if (href === null || href === "") return
    this.editor?.chain().focus().setButton({ href, label }).run()
  }

  insertMergeTag(event: Event) {
    const tag = (event.currentTarget as HTMLElement).dataset.tag
    if (tag) {
      this.editor?.chain().focus().insertContent(tag).run()
    }
  }

  toggleMergeMenu(event: Event) {
    event.stopPropagation()
    const menu = this.element.querySelector("[data-merge-menu]") as HTMLElement
    if (menu) {
      menu.classList.toggle("hidden")
      // Close on next click outside
      const close = () => { menu.classList.add("hidden"); document.removeEventListener("click", close) }
      if (!menu.classList.contains("hidden")) {
        setTimeout(() => document.addEventListener("click", close), 0)
      }
    }
  }

  toggleSource() {
    if (!this.hasSourceTarget) return

    this.sourceMode = !this.sourceMode
    if (this.sourceMode) {
      this.sourceTarget.value = this.editor?.getHTML() || ""
      this.editorTarget.classList.add("hidden")
      this.sourceTarget.classList.remove("hidden")
      this.toolbarTarget.classList.add("opacity-50", "pointer-events-none")
    } else {
      this.editor?.commands.setContent(this.sourceTarget.value)
      this.inputTarget.value = this.sourceTarget.value
      this.editorTarget.classList.remove("hidden")
      this.sourceTarget.classList.add("hidden")
      this.toolbarTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }
}
